import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import React, { useState } from 'react';
import type { RegistryOfferDocument } from '../../api/registryDealTypes';

interface Props {
  documents: RegistryOfferDocument[];
  readOnly: boolean;
  busy: boolean;
  onRename: (documentId: string, title: string) => void;
  onRemove: (documentId: string) => void;
  onReorder: (documentIds: string[]) => void;
}

function moved(ids: string[], from: number, to: number): string[] {
  const next = [...ids];
  const [id] = next.splice(from, 1);
  next.splice(to, 0, id);
  return next;
}

function RenameField({
  document,
  busy,
  onDone,
  onRename,
}: {
  document: RegistryOfferDocument;
  busy: boolean;
  onDone: () => void;
  onRename: Props['onRename'];
}) {
  const [title, setTitle] = useState(document.title);
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexGrow: 1 }}>
      <TextField
        size="small"
        label="Document title"
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        inputProps={{ maxLength: 120 }}
        sx={{ flexGrow: 1 }}
      />
      <Button
        size="small"
        variant="contained"
        disabled={busy || !title.trim()}
        onClick={() => {
          onRename(document.externalId, title.trim());
          onDone();
        }}
      >
        Save
      </Button>
      <Button size="small" onClick={onDone}>
        Cancel
      </Button>
    </Stack>
  );
}

function DocumentControls({
  document,
  index,
  ids,
  busy,
  onRename,
  onRemove,
  onReorder,
}: {
  document: RegistryOfferDocument;
  index: number;
  ids: string[];
  busy: boolean;
  onRename: () => void;
  onRemove: Props['onRemove'];
  onReorder: Props['onReorder'];
}) {
  return (
    <Stack direction="row" spacing={0.5}>
      <Button
        size="small"
        disabled={busy || index === 0}
        aria-label={`Move ${document.title} up`}
        onClick={() => onReorder(moved(ids, index, index - 1))}
      >
        Up
      </Button>
      <Button
        size="small"
        disabled={busy || index === ids.length - 1}
        aria-label={`Move ${document.title} down`}
        onClick={() => onReorder(moved(ids, index, index + 1))}
      >
        Down
      </Button>
      <Button
        size="small"
        disabled={busy}
        aria-label={`Rename ${document.title}`}
        onClick={onRename}
      >
        Rename
      </Button>
      <Button
        size="small"
        color="error"
        disabled={busy}
        aria-label={`Remove ${document.title}`}
        onClick={() => onRemove(document.externalId)}
      >
        Remove
      </Button>
    </Stack>
  );
}

/**
 * The offer's documents, in the order investors see them. Uploaded PDFs show by title only; their
 * storage location stays on the server. Links also show their address.
 */
export default function OfferDocumentsList({
  documents,
  readOnly,
  busy,
  onRename,
  onRemove,
  onReorder,
}: Props) {
  const [renaming, setRenaming] = useState<string | null>(null);
  const ids = documents.map((document) => document.externalId);

  if (!documents.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No documents yet.
      </Typography>
    );
  }

  return (
    <Stack component="ol" spacing={1} sx={{ m: 0, p: 0, listStyle: 'none' }}>
      {documents.map((document, index) => (
        <Box
          component="li"
          key={document.externalId}
          aria-label={document.title}
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
            p: 1,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          {renaming === document.externalId ? (
            <RenameField
              document={document}
              busy={busy}
              onRename={onRename}
              onDone={() => setRenaming(null)}
            />
          ) : (
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600}>
                {document.title}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                {document.kind === 'LINK' ? document.url : 'Uploaded PDF'}
              </Typography>
            </Box>
          )}
          {!readOnly && renaming !== document.externalId && (
            <DocumentControls
              document={document}
              index={index}
              ids={ids}
              busy={busy}
              onRename={() => setRenaming(document.externalId)}
              onRemove={onRemove}
              onReorder={onReorder}
            />
          )}
        </Box>
      ))}
    </Stack>
  );
}
